import styled, { DefaultTheme } from 'styled-components';
import { Icon } from '../icon';
import { buttonStyle, linkButtonStyle } from './buttonStyle';

export type ButtonSizeType = 'small' | 'medium' | 'large' | 'fullWidth';
export type ButtonType = 'default' | 'icon';

type ButtonStyleProps = {
    $size?: ButtonSizeType;
    size?: ButtonSizeType;
    $type?: ButtonType;
};

type ButtonStylePropsWithTheme = ButtonStyleProps & { theme: DefaultTheme };

type LinkButtonStyleProps = ButtonStyleProps & {
    $fullWidth?: boolean;
    fullWidth?: boolean;
    $customWidth?: string;
    $disabled?: boolean;
    disabled?: boolean;
    tabIndex?: number;
};

type LinkStyleProps = {
    $isHidden?: boolean;
    $buttonStyle?: boolean;
    $disabled?: boolean;
    disabled?: boolean;
    tabIndex?: number;
};

const transientButtonProps = new Set(['$size', 'size', '$type']);
const transientLinkButtonProps = new Set(['$size', 'size', '$type', 'fullWidth', 'disabled']);
const transientLinkProps = new Set(['disabled']);
const getButtonSize = (props: ButtonStyleProps) => props.$size ?? props.size;
const getButtonType = (props: ButtonStyleProps) => props.$type ?? 'default';
const getIconButtonSize = (props: ButtonStylePropsWithTheme) => {
    const size = getButtonSize(props);

    if (size === 'large') return props.theme.app.component.button.large.height;
    if (size === 'medium') return props.theme.app.component.button.medium.height;
    if (size === 'small') return props.theme.app.component.button.small.height;

    return props.theme.app.component.button.medium.height;
};
const getLinkButtonDisabled = (props: LinkButtonStyleProps) => props.$disabled ?? props.disabled;
const getLinkButtonFullWidth = (props: LinkButtonStyleProps) => props.$fullWidth ?? props.fullWidth;
const getLinkDisabled = (props: LinkStyleProps) => props.$disabled ?? props.disabled;

export const Button = styled.button.withConfig({
    shouldForwardProp: (prop) => !transientButtonProps.has(prop)
})<ButtonStyleProps>`
    ${buttonStyle};
    border-radius: ${(props) => props.theme.app.component.button.borderRadius};
    font-size: ${(props) => props.theme.app.component.button.fontSize};
    font-weight: ${(props) => props.theme.app.component.button.fontWeight};
    display: inline-flex;
    gap: 5px;

    ${(props) =>
        getButtonSize(props) === 'fullWidth' &&
        `
		display:inline-flex;
		gap:12px;
		width:100%;
   `}

    ${(props) =>
        getButtonSize(props) === 'large' &&
        `
		min-width: ${props.theme.app.component.button.large.width};
		height: ${props.theme.app.component.button.large.height};
		min-height: ${props.theme.app.component.button.large.height};
		padding: ${props.theme.app.component.button.large.padding};
	`}

	${(props) =>
        getButtonSize(props) === 'medium' &&
        `
		min-width: ${props.theme.app.component.button.medium.width};
		height: ${props.theme.app.component.button.medium.height};
		min-height: ${props.theme.app.component.button.medium.height};
		padding: ${props.theme.app.component.button.medium.padding};
  `}

  ${(props) =>
        getButtonSize(props) === 'small' &&
        `
    	min-width: ${props.theme.app.component.button.small.width};
        height: ${props.theme.app.component.button.small.height};
    	min-height: ${props.theme.app.component.button.small.height};
    	padding: ${props.theme.app.component.button.small.padding};
  `}

  ${(props) =>
        getButtonType(props) === 'icon' &&
        `
        min-width: ${getIconButtonSize(props)};
        width: ${getIconButtonSize(props)};
        height: ${getIconButtonSize(props)};
        min-height: ${getIconButtonSize(props)};
        padding: 0;
        gap: 0;
        justify-content: center;
    `}

  &:focus,
  &:active {
        outline: none;
    }

    /* An icon dropped straight into a button takes the label's colour, the way the Icon widget
       paints one. A glyph that declares its own stroke is drawn with that stroke instead, or the
       fill below would flood an outline shape solid. */
    > svg {
        width: 16px;
        height: 16px;
        fill: currentColor;
    }

    > svg[stroke='currentColor'] {
        fill: none;
        stroke: currentColor;
    }

    ${Icon} {
        width: 16px;
        height: 16px;
    }
`;

export const BasicButton = styled(Button)`
    background-color: ${(props) => props.theme.app.component.button.basic.default.backgroundColor};
    border: ${(props) => props.theme.app.component.button.basic.border};
    color: ${(props) => props.theme.app.component.button.basic.default.textColor};

    &[disabled] {
        opacity: ${(props) => props.theme.app.component.button.basic.disabled.opacity};
        background-color: ${(props) => props.theme.app.component.button.basic.disabled.backgroundColor};
        color: ${(props) => props.theme.app.component.button.basic.disabled.textColor};
        border: ${(props) => props.theme.app.component.button.basic.disabled.border};
    }

    &:hover:not([disabled]) {
        background-color: ${(props) => props.theme.app.component.button.basic.hover.backgroundColor};
        color: ${(props) => props.theme.app.component.button.basic.hover.textColor};
    }

    &:active:not([disabled]) {
        background-color: ${(props) => props.theme.app.component.button.basic.active.backgroundColor};
        color: ${(props) => props.theme.app.component.button.basic.active.textColor};
    }
`;

export const GhostButton = styled(Button)`
    background-color: transparent;
    border: 1px solid transparent;
    color: #263238;

    &[disabled] {
        opacity: ${(props) => props.theme.app.component.button.basic.disabled.opacity};
        background-color: transparent;
        color: #8fa4ae;
        border: 1px solid transparent;
    }

    &:hover:not([disabled]) {
        background-color: #e5e7eb;
        color: #263238;
    }

    &:active:not([disabled]) {
        background-color: #d1d5db;
        color: #263238;
    }
`;

// OUTLINE: BORDO GRIGIO, FONDO BIANCO, E PASSANDOCI SOPRA SI ACCENDE SOLO IL BORDO (utente, 2026-10-14).
// È il bottone dei comandi in colonna — quelli di una selezione multipla nello Scene Editor e nel
// Composer —: dove i bottoni sono tanti, uno sotto l'altro, Basic con il suo fondo pieno al passaggio
// pesa troppo. `$danger` colora la parola di rosso per Remove, senza farne un Destructive pieno.
export const OutlineButton = styled(Button)<{ $danger?: boolean }>`
    justify-content: center;
    border: 1px solid ${(props) => props.theme.app.borderColor};
    background-color: ${(props) => props.theme.app.backgroundColor};
    color: ${(props) => (props.$danger ? props.theme.app.component.button.destructive.default.backgroundColor : props.theme.app.textColor)};
    font-weight: 400;

    &[disabled] {
        opacity: ${(props) => props.theme.app.component.button.basic.disabled.opacity};
        cursor: default;
    }

    /* Il bottone base, al passaggio, si tinge del colore primario: qui cambia solo il bordo. */
    &:hover:not([disabled]),
    &:active:not([disabled]),
    &:focus-visible {
        border-color: ${(props) => props.theme.app.secondary};
        background-color: ${(props) => props.theme.app.backgroundColor};
        color: ${(props) => (props.$danger ? props.theme.app.component.button.destructive.default.backgroundColor : props.theme.app.textColor)};
    }
`;

export const HighlightBasicButton = styled(BasicButton)<{ $highlight?: boolean }>`
    ${(props) =>
        props.$highlight &&
        `
        color: #d32f2f;
        border-color: #ffaaaa;
        animation: highlightAssignBlink 0.8s ease-in-out 7;
        @keyframes highlightAssignBlink {
            0%, 100% {
                box-shadow: none;
                border-color: #ffaaaa;
            }
            50% {
                box-shadow: 0 3px 16px rgba(211, 47, 47, 0.4), 0 8px 36px rgba(211, 47, 47, 0.3);
                border-color: #d32f2f;
            }
        }
    `}
`;

export const PrimaryButton = styled(Button)`
    background-color: ${(props) => props.theme.app.component.button.primary.default.backgroundColor};
    border: ${(props) => props.theme.app.component.button.primary.border};
    color: ${(props) => props.theme.app.component.button.primary.default.textColor};

    &[disabled] {
        opacity: ${(props) => props.theme.app.component.button.primary.disabled.opacity};
        background-color: ${(props) => props.theme.app.component.button.primary.disabled.backgroundColor};
        color: ${(props) => props.theme.app.component.button.primary.disabled.textColor};
        border: ${(props) => props.theme.app.component.button.primary.disabled.border};
    }

    &:hover:not([disabled]) {
        background-color: ${(props) => props.theme.app.component.button.primary.hover.backgroundColor};
        color: ${(props) => props.theme.app.component.button.primary.hover.textColor};
    }

    &:active:not([disabled]) {
        background-color: ${(props) => props.theme.app.component.button.primary.active.backgroundColor};
        color: ${(props) => props.theme.app.component.button.primary.active.textColor};
    }
`;

export const DestructiveButton = styled(Button)`
    background-color: ${(props) => props.theme.app.component.button.destructive.default.backgroundColor};
    border: ${(props) => props.theme.app.component.button.destructive.border};
    color: ${(props) => props.theme.app.component.button.destructive.default.textColor};

    &[disabled] {
        opacity: ${(props) => props.theme.app.component.button.destructive.disabled.opacity};
        background-color: ${(props) => props.theme.app.component.button.destructive.disabled.backgroundColor};
        color: ${(props) => props.theme.app.component.button.destructive.disabled.textColor};
        border: ${(props) => props.theme.app.component.button.destructive.disabled.border};
    }

    &:hover:not([disabled]) {
        background-color: ${(props) => props.theme.app.component.button.destructive.hover.backgroundColor};
        color: ${(props) => props.theme.app.component.button.destructive.hover.textColor};
    }

    &:active:not([disabled]) {
        background-color: ${(props) => props.theme.app.component.button.destructive.active.backgroundColor};
        color: ${(props) => props.theme.app.component.button.destructive.active.textColor};
    }
`;

// https://www.wrike.com/open.htm?id=1096711490
export const LinkButton = styled.a
    .withConfig({
        shouldForwardProp: (prop) => !transientLinkButtonProps.has(prop)
    })
    .attrs<LinkButtonStyleProps>((props) => {
        const disabled = getLinkButtonDisabled(props);

        return {
            $disabled: disabled,
            'aria-disabled': disabled ? true : undefined,
            tabIndex: disabled ? -1 : props.tabIndex
        };
    })<LinkButtonStyleProps>`
    ${linkButtonStyle};
    display: ${(props) => (getLinkButtonFullWidth(props) ? 'flex' : 'inline-flex')};
    width: ${(props) => (getLinkButtonFullWidth(props) ? '100%' : props.$customWidth ? props.$customWidth : 'auto')};

    ${(props) =>
        getButtonSize(props) === 'large' &&
        `
		min-width: ${props.theme.app.component.button.large.width};
		height: ${props.theme.app.component.button.large.height};
		min-height: ${props.theme.app.component.button.large.height};
		padding: ${props.theme.app.component.button.large.padding};
	`}

    ${(props) =>
        getButtonSize(props) === 'medium' &&
        `
		min-width: ${props.theme.app.component.button.medium.width};
		height: ${props.theme.app.component.button.medium.height};
		min-height: ${props.theme.app.component.button.medium.height};
		padding: ${props.theme.app.component.button.medium.padding};
  `}

    ${(props) =>
        getButtonSize(props) === 'small' &&
        `
        min-width: ${props.theme.app.component.button.small.width};
        height: ${props.theme.app.component.button.small.height};
        min-height: ${props.theme.app.component.button.small.height};
        padding: ${props.theme.app.component.button.small.padding};
    `}

    ${(props) =>
        getButtonType(props) === 'icon' &&
        `
        min-width: ${getIconButtonSize(props)};
        width: ${getIconButtonSize(props)};
        height: ${getIconButtonSize(props)};
        min-height: ${getIconButtonSize(props)};
        padding: 0;
        gap: 0;
        justify-content: center;
    `}

    &:focus,
    &:active {
        outline: none;
    }

    > svg {
        width: 16px;
        height: 16px;
        margin-right: 10px;
    }

    ${(props) =>
        getLinkButtonDisabled(props) &&
        `
        opacity: ${props.theme.app.component.button.basic.disabled.opacity};
        background-color: ${props.theme.app.component.button.basic.disabled.backgroundColor};
        color: ${props.theme.app.component.button.basic.disabled.textColor};
        border: ${props.theme.app.component.button.basic.disabled.border};
        pointer-events: none;
    `}
`;

export const OutlineLinkButton = styled(LinkButton)`
    background-color: white !important;
    border: 1px ${(props) => props.theme.button.color} solid !important;
    color: ${(props) => props.theme.button.color} !important;

    ${(props) =>
        getLinkButtonDisabled(props) &&
        `
        opacity: 0.5;
    `}

    ${(props) =>
        !getLinkButtonDisabled(props) &&
        `
    &:hover {
        background-color: ${props.theme.button.color}!important;
        color: white !important;
    }
    `}
`;

export const Link = styled.a
    .withConfig({
        shouldForwardProp: (prop) => !transientLinkProps.has(prop)
    })
    .attrs<LinkStyleProps>((props) => {
        const disabled = getLinkDisabled(props);

        return {
            $disabled: disabled,
            'aria-disabled': disabled ? true : undefined,
            tabIndex: disabled ? -1 : props.tabIndex
        };
    })<LinkStyleProps>`
    ${(props) =>
        !getLinkDisabled(props) &&
        `
      color: ${props.theme.app.component.link.color};
      text-decoration: ${props.theme.app.component.link.decoration};
      cursor: pointer;
    `};

    ${(props) =>
        getLinkDisabled(props) &&
        `
      color: gray;
      text-decoration: none;
      cursor: default;

      &:hover {
          text-decoration: none;
      }
    `};

    ${(props) => (props.$isHidden ? 'visibility: hidden;' : 'visibility: visible;')};
    ${(props) => (props.$buttonStyle ? linkButtonStyle : '')};
`;

export const LinkBasicButton = styled(LinkButton)`
    background-color: ${(props) => props.theme.app.component.button.basic.default.backgroundColor};
    border: ${(props) => props.theme.app.component.button.basic.border};
    color: ${(props) => props.theme.app.component.button.basic.default.textColor};

    ${(props) =>
        getLinkButtonDisabled(props) &&
        `
        opacity: ${props.theme.app.component.button.basic.disabled.opacity};
        background-color: ${props.theme.app.component.button.basic.disabled.backgroundColor};
        color: ${props.theme.app.component.button.basic.disabled.textColor};
        border: ${props.theme.app.component.button.basic.disabled.border};
    `}

    ${(props) =>
        !getLinkButtonDisabled(props) &&
        `
    &:hover {
        background-color: ${props.theme.app.component.button.basic.hover.backgroundColor};
        color: ${props.theme.app.component.button.basic.hover.textColor};
    }
    `}

    &:active {
        background-color: ${(props) => props.theme.app.component.button.basic.active.backgroundColor};
        color: ${(props) => props.theme.app.component.button.basic.active.textColor};
    }
`;
